// What an order knows, for the three screens that carry an order's key and not
// its details.
//
// WHY THIS EXISTS RATHER THAN A JOIN. The API resolves names server-side for the
// four barrier views and not for the plain collections, so a two-week row
// arrives carrying `order_id` and nothing else. Three screens need more than
// the number: the source shows a job number, a customer name and a product on
// the Two Week Production View, and a product is reachable only through the
// order -- D15 dropped `Two Week Production.Product Type` on the finding that
// all 49 of its values pointed at `Products` keys that do not exist.
//
// WHY IT RESOLVES INTO THE ROWS RATHER THAN AT RENDER TIME. A grid column that
// computes its value from a lookup produces a row whose value changes after the
// first render, when the lookup arrives -- and the grouping is computed from the
// first one and cached. That is exactly the defect the sign-off found on the
// Daily Production Tracker Table: the group headers showed raw order ids while
// the column beneath them showed job numbers. Resolving into the data gives the
// table a new array to group, so the header and the cells cannot disagree.

import { useMemo } from 'react';
import { useCollection, type Async } from '../data';
import type { Row } from '../types';

export interface OrderFacts {
  order_number: string | null;
  customer_name: string | null;
  product_type: string | null;
}

export type OrderIndex = Map<number, OrderFacts>;

export function useOrderIndex(): Async<OrderIndex> {
  const orders = useCollection('orders');
  const products = useCollection('products');

  const data = useMemo<OrderIndex | null>(() => {
    if (!orders.data || !products.data) return null;
    const productNames = new Map<number, string>();
    for (const product of products.data) {
      if (typeof product.product_id === 'number') {
        productNames.set(product.product_id, String(product.product_type ?? ''));
      }
    }
    const index: OrderIndex = new Map();
    for (const order of orders.data) {
      if (typeof order.order_id !== 'number') continue;
      index.set(order.order_id, {
        order_number: order.order_number === null ? null : String(order.order_number),
        customer_name: order.customer_name === null ? null : String(order.customer_name ?? ''),
        product_type:
          typeof order.product_id === 'number' ? productNames.get(order.product_id) ?? null : null,
      });
    }
    return index;
  }, [orders.data, products.data]);

  return {
    data,
    error: orders.error ?? products.error,
    loading: orders.loading || products.loading,
    reload: () => {
      orders.reload();
      products.reload();
    },
  };
}

/** Each row with its order's facts folded in, under the names the columns use. */
export function withOrderFacts<T extends Row>(rows: T[], index: OrderIndex | null): T[] {
  if (!index) return rows;
  return rows.map((row) => {
    const facts = typeof row.order_id === 'number' ? index.get(row.order_id) : undefined;
    return {
      ...row,
      order_number: facts?.order_number ?? null,
      order_customer_name: facts?.customer_name ?? null,
      order_product_type: facts?.product_type ?? null,
    };
  });
}
